import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

function formatApiError(detail, fallback = "Something went wrong.") {
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return detail?.msg || JSON.stringify(detail);
}

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  const redirectAfter = (user) => {
    const next = new URLSearchParams(location.search).get("next");
    if (user.role === "owner") return navigate("/admin");
    if (user.subscription_status !== "active") return navigate("/subscribe");
    if (!user.onboarded) return navigate("/onboarding");
    navigate(next || "/dashboard");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(null);
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.access_token) localStorage.setItem("session_token", data.access_token);
      setUser(data.user);
      toast.success(`Welcome back, ${data.user.name.split(" ")[0]}.`);
      redirectAfter(data.user);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail, "Login failed."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-sg-paper flex flex-col">
      <header className="bg-sg-navy text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-20 flex items-center">
          <Link to="/" className="flex items-center" data-testid="brand-logo">
            <img src="/inpropportal-logo.png" alt="INPropPortal.com" className="h-11 w-auto" />
          </Link>
        </div>
      </header>
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-2">
        <div className="hidden lg:block relative overflow-hidden bg-sg-navy text-white">
          <div className="absolute inset-0 bg-cover bg-center opacity-30"
               style={{ backgroundImage: "url(https://images.pexels.com/photos/27826399/pexels-photo-27826399.jpeg)" }} />
          <div className="absolute inset-0 bg-gradient-to-tr from-sg-navy-deep via-sg-navy/80 to-transparent" />
          <div className="relative h-full flex flex-col justify-between p-12">
            <div>
              <div className="overline text-sg-gold mb-4">PUNE · MAHARASHTRA · IN</div>
              <h2 className="font-display text-5xl leading-[1.05]">
                For agents who close deals, not <span className="font-serif-italic text-sg-gold">chase leads.</span>
              </h2>
            </div>
            <div className="space-y-3 text-sm text-white/75 max-w-md">
              <p>&ldquo;Our brokerage onboarded 14 agents in one week. The WhatsApp ingestion alone saved us ~6 hours a day.&rdquo;</p>
              <p className="font-mono text-xs text-sg-gold">— Operations Lead, Kharadi-based brokerage</p>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-center p-8 bg-sg-cream-soft">
          <form className="w-full max-w-sm" onSubmit={onSubmit} data-testid="login-form">
            <div className="overline text-sg-gold-deep mb-3">AGENT &amp; OWNER SIGN-IN</div>
            <h1 className="font-display text-5xl text-sg-navy">Welcome back.</h1>
            <p className="mt-3 text-sg-mute">Sign in with the email and password you registered.</p>

            <div className="mt-8 space-y-4">
              <div>
                <Label htmlFor="email" className="text-xs uppercase tracking-wider text-sg-mute">Email</Label>
                <Input
                  id="email" type="email" required value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  className="mt-1 h-12 bg-white"
                  data-testid="login-email"
                />
              </div>
              <div>
                <Label htmlFor="password" className="text-xs uppercase tracking-wider text-sg-mute">Password</Label>
                <Input
                  id="password" type="password" required value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  className="mt-1 h-12 bg-white"
                  data-testid="login-password"
                />
              </div>
              {err && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="login-error">
                  {err}
                </div>
              )}
              <Button
                type="submit"
                size="lg"
                disabled={submitting}
                className="w-full h-12 rounded-full bg-sg-navy hover:bg-sg-navy-700 text-white"
                data-testid="login-submit"
              >
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Continue <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
            <p className="mt-6 text-xs text-sg-mute leading-relaxed">
              New here? <Link to="/register" className="text-sg-gold-deep border-b border-sg-gold" data-testid="login-register-link">Create an agent account</Link> · ₹999 / month
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
